import {
  ActionCreators,
  AlarmUtils,
  DateTimeUtils,
  Selectors
} from 'galarm-shared'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Header,
  Text,
  Menu,
  Image
} from 'grommet'
import { Close, MoreVertical } from 'grommet-icons'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import LabelledText from './LabelledText'
import isEmpty from 'lodash/isEmpty'
import { Constants, GlobalConfig, I18n } from 'galarm-config'
import LabelText from './LabelText'
import { NavigationUtils } from 'galarm-ps-api'
import AppCanvas from './AppCanvas'
import AlarmType from './AlarmType'
import ColoredButton from './ColoredButton'
import objOmit from 'lodash/omit'
import EntityNotFound from './EntityNotFound'
import RichTextEditor from './RichTextEditor'

const { makeIncidentAlarmSelector, makeParticipantAlarmSelector } = Selectors

const IncidentAlarmDetailsHeader = ({ alarm }) => {
  const dispatch = useDispatch()

  const closeIncidentAlarmDetailsScreen = () => {
    dispatch(ActionCreators.hideIncidentAlarmDetailsScreen())
  }

  const onReportProblem = () => {
    GlobalConfig.showFeedbackScreen({
      type: Constants.TICKET_TYPES.PROBLEM_REPORT,
      extras: JSON.stringify({
        alarm: objOmit(alarm, [
          'name',
          'notes',
          'backupGroup',
          'backupContacts'
        ])
      })
    })
  }

  return (
    <Header className="header" background="brand" pad="small">
      <Text color="white">{I18n.t('alarmDetails')}</Text>
      <Box direction="row">
        <Button icon={<Close />} onClick={closeIncidentAlarmDetailsScreen} />
        <Menu
          dropBackground="lightTint"
          icon={<MoreVertical />}
          items={[
            {
              label: I18n.t('reportAProblem'),
              onClick: onReportProblem
            }
          ]}
        />
      </Box>
    </Header>
  )
}

const IncidentAlarmDetails = ({ alarmId }) => {
  const dispatch = useDispatch()

  const incidentAlarmSelector = makeIncidentAlarmSelector()
  const participantAlarmSelector = makeParticipantAlarmSelector()

  let incidentAlarm = useSelector(state =>
    incidentAlarmSelector(state, { alarmId: alarmId })
  )
  const participantAlarm = useSelector(state =>
    participantAlarmSelector(state, { alarmId: alarmId })
  )

  const alarm = !isEmpty(incidentAlarm) ? incidentAlarm : participantAlarm

  const onUnableToFetchAlarm = () => {
    dispatch(ActionCreators.removeIncidentAlarm(alarmId))
  }

  if (isEmpty(alarm)) {
    return (
      <EntityNotFound
        entityType={I18n.t('entityAlarm')}
        onClose={() =>
          dispatch(ActionCreators.hideIncidentAlarmDetailsScreen())
        }
        onUnableToFetch={onUnableToFetchAlarm}
      />
    )
  }

  const alarmDate = alarm.date

  const alarmAcknowledgement =
    AlarmUtils.getIncidentAlarmAcknowledgementStatusForOccurrence(
      alarm.id,
      alarm.dateString
    )

  const onGroupAlarmYes = () => {
    if (
      alarmAcknowledgement &&
      alarmAcknowledgement.response === Constants.GROUP_ALARM_YES
    ) {
      NavigationUtils.showAlert(I18n.t('alarmAlreadyConfirmed'))
      return
    }

    onGroupAlarmYesCore(true)
  }

  const onGroupAlarmYesCore = () => {
    dispatch(
      ActionCreators.markIncidentAlarmResponse(
        alarm.id,
        alarm.dateString,
        Date.now(),
        Constants.GROUP_ALARM_YES
      )
    )
    dispatch(ActionCreators.hideIncidentAlarmDetailsScreen())
  }

  return (
    <AppCanvas key={alarmId} overflow="auto">
      <IncidentAlarmDetailsHeader />
      <Card
        margin="small"
        background="textBackgroundColor"
        width="600px"
        flex={{ shrink: 0 }}>
        <CardHeader pad="small" justify="between">
          <Box flex />
          <AlarmType
            type={alarm.type}
            creationMode={alarm.creationMode}
            source={alarm.source}
          />
          <Box flex />
        </CardHeader>
        <CardBody gap="medium" pad="medium">
          <Box direction="row" gap="medium">
            <LabelledText
              label={I18n.t('date')}
              text={DateTimeUtils.getDateAsString(alarmDate)}
              border="bottom"
            />
            <LabelledText
              label={I18n.t('time')}
              text={DateTimeUtils.getTimeAsString(alarmDate)}
              border="bottom"
            />
          </Box>
          <LabelledText
            label={I18n.t('title')}
            text={alarm.name}
            border="bottom"
            flex={{ shrink: 0 }}
          />
          {(!!alarm.notes || !!alarm.alarmPhotoUrl) && (
            <Box gap="medium">
              <Box>
                <LabelText color="darkTint">{I18n.t('notes')}</LabelText>
                {alarm.alarmPhotoUrl && (
                  <Box
                    margin={{
                      vertical: 'small',
                      left: 'large'
                    }}
                    width="80%">
                    <Image src={alarm.alarmPhotoUrl} />
                  </Box>
                )}
                {!!alarm.notes && (
                  <Box direction="row" border="bottom">
                    <Text style={{ wordBreak: 'break-all' }}>
                      <RichTextEditor
                        notes={alarm.notes}
                        readOnly="true"
                        toolbarHidden="true"
                      />
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </CardBody>
        <CardFooter pad="small" background="brand">
          <Box flex direction="row" justify="around">
            <ColoredButton
              color="white"
              label={I18n.t(Constants.GROUP_ALARM_YES)}
              onClick={onGroupAlarmYes}
            />
          </Box>
        </CardFooter>
      </Card>
    </AppCanvas>
  )
}

export default IncidentAlarmDetails
